"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PageLoadProvider = exports.usePageLoadApi = exports.PageLoadContext = void 0;
var React = __importStar(require("react"));
var react_1 = require("react");
var router_1 = require("next/router");
var LoggerFactory_1 = require("./utils/LoggerFactory");
var sli_react_metrics_1 = require("@cvent/sli-react-metrics");
var useCoreWebvitals_1 = __importDefault(require("./useCoreWebvitals"));
var LOG = LoggerFactory_1.LoggerFactory.create('PageLoad');
var LOG2 = LoggerFactory_1.LoggerFactory.create('PageActions');
var lastTrackUrl = '';
var noop = function () { return undefined; };
/**
 * Provide a default context so that the hook can be used in jest or if
 * page load metrics are disabled
 */
exports.PageLoadContext = React.createContext({
    url: '',
    pathname: '',
    getBaseProps: noop,
    trackPageLoad: noop,
    cancelMeasurePageLoad: noop,
    initMetrics: noop,
    measurePageRefetch: noop,
    registerPageAction: noop,
    unregisterPageAction: noop,
    registerPageActionCallback: noop
});
var usePageLoadApi = function () { return (0, react_1.useContext)(exports.PageLoadContext); };
exports.usePageLoadApi = usePageLoadApi;
var PageLoadProvider = function (_a) {
    var children = _a.children, config = _a.config, _b = _a.includeWebVitals, includeWebVitals = _b === void 0 ? false : _b, _c = _a.customTimingLabel, customTimingLabel = _c === void 0 ? 'PGQL' : _c;
    var _d = (0, sli_react_metrics_1.usePageActions)(), endAction = _d.endAction, actionsRef = _d.actionsRef, getBaseProps = _d.getBaseProps;
    var measureEvent = (0, sli_react_metrics_1.useMetrics)().measureEvent;
    var router = (0, router_1.useRouter)();
    // Store state in a ref, so that all our callbacks and listeners do not
    // need to use dependencies.
    var stateRef = (0, react_1.useRef)({});
    stateRef.current = {
        currentPageLoadUrl: stateRef.current.currentPageLoadUrl,
        pathname: router.pathname,
        query: router.query,
        url: router.basePath || router.asPath ? "".concat(router.basePath || '').concat(router.asPath || '') : null,
        excludedUrls: (config === null || config === void 0 ? void 0 : config.excludedUrls) || [],
        excludedPaths: (config === null || config === void 0 ? void 0 : config.excludedPaths) || []
    };
    var eventListenersRef = (0, react_1.useRef)({});
    var metricEventsRef = (0, react_1.useRef)({});
    var didPageTrackLoad = (0, react_1.useRef)(false);
    var additionalData = (0, react_1.useRef)((0, sli_react_metrics_1.getPersistedData)() || {});
    /**
     * Registers page actions so that we can keep track of them and warn consumers in case
     * they are not tracked before the app swtiches to a different route
     */
    var registerPageAction = (0, react_1.useCallback)(function (props) {
        var action = props.action, metric = props.metric;
        metricEventsRef.current[action] = metric;
    }, []);
    /**
     * Unregisters page actions to stop watching them and avoid false negatives abandoned events
     */
    var unregisterPageAction = (0, react_1.useCallback)(function (action) {
        // Grab the metrics client using the action name
        var metric = metricEventsRef.current[action];
        if (metric) {
            delete metricEventsRef.current[action];
        }
    }, []);
    /**
     * Registers page actions so that we can keep track of them and delegate to consumers in case
     * they are not tracked before the app swtiches to a different route
     */
    var registerPageActionCallback = (0, react_1.useCallback)(function (props) {
        var action = props.action, callback = props.callback;
        eventListenersRef.current[action] = callback;
    }, []);
    /**
     * When the page is initially loaded, We must have an instance of the metrics client.
     * This function is used by the useTrackPageLoad hook to check if a page is trying
     * to record a metric before the metrics client has been initialized to avoid sending
     * missleading not_tracked events for pages that actually were tracked.
     *
     * NOTE: The initialization was moved to the hook given that some pages are not properly
     * setting the loading property (passing false) which makes the page endup in a state where
     * is trying to track a page_load event without having an instance of the metrics client.
     *
     */
    var initMetrics = (0, react_1.useCallback)(function (url) {
        var metric = metricEventsRef.current[url];
        var pathname = url === stateRef.current.url ? stateRef.current.pathname : undefined;
        // Only initialize the client if we don't have one
        if (!metric && !didPageTrackLoad.current) {
            LOG.debug("Measuring page load event for url => ".concat(url));
            stateRef.current.currentPageLoadUrl = url;
            metricEventsRef.current[url] = measureEvent('page_load', __assign({ url: url, pathname: pathname, trigger: 'browser_or_app_load' }, additionalData.current));
        }
    }, [measureEvent]);
    /**
     * Function to allow pages record/measure subsequent page loads. Useful for when
     * pages grab data from the cache then network or when pages refetch for data with
     * a different set of variables
     */
    var measurePageRefetch = (0, react_1.useCallback)(function (url) {
        LOG.debug("Measuring page refetch for url => ".concat(url));
        stateRef.current.currentPageLoadUrl = url;
        var pathname = url === stateRef.current.url ? stateRef.current.pathname : undefined;
        metricEventsRef.current[url] = measureEvent('page_load', __assign({ url: url, pathname: pathname, trigger: 'page_subsequent_load' }, additionalData.current));
    }, [measureEvent]);
    /**
     * Method to check if we can record the metric for the current url.
     * Note that for page_load events, we save it in a ref (didPageTrackLoad.current = true) to avoid
     * tracking a second page_load due to a page re-rendering. We're only interested on the initial load
     * but we still record errors if the page second fetch failed for some reason
     */
    var recordPageLoad = (0, react_1.useCallback)(function (url, result, data) {
        if (result === void 0) { result = 'success'; }
        var defaultType = result === 'success' || result === 'incomplete_measure' ? 'tracked' : 'not_tracked';
        var LOGGER = LOG;
        var message = "Page finished loading for => ".concat(url, ", [").concat(result, "][").concat(defaultType, "]");
        // Always get the latest persisted data
        additionalData.current = __assign(__assign({}, additionalData.current), (0, sli_react_metrics_1.getPersistedData)());
        if (!(data === null || data === void 0 ? void 0 : data.skip))
            LOGGER.debug(message);
        // Grab the metrics client using the eventName and fallback to url
        var metric = metricEventsRef.current[url];
        if (metric) {
            // The pathname is not known immediately on routeChangeStart, so it is captured
            // in routeChangeComplete. But sometimes trackPageLoad is called prior to routeChangeComplete,
            // so we fallback to the current pathname if we have one for the url being tracked.
            // This might be able to assume stateRef.current.pathname is correct, but to be conservative
            // and safe we always check that the url matches in case the route changes just before a
            // cal to trackPageLoad for some reason.
            var pathname = metric.getData().pathname;
            if (!pathname && url === stateRef.current.url) {
                pathname = stateRef.current.pathname;
            }
            // If this metric is for a custom event we always want to record it otherwise we check if either
            // the url or path is not in the excluded arrays to record the event
            if (!stateRef.current.excludedUrls.includes(url) && !stateRef.current.excludedPaths.includes(pathname)) {
                metric.record(result, __assign(__assign(__assign({ type: defaultType }, data), { pathname: pathname }), additionalData.current));
            }
            else {
                LOGGER.debug("Page load excluded url ".concat(url, " due to excludedUrls or excludedPaths config."));
            }
            if (!(data === null || data === void 0 ? void 0 : data.skip)) {
                // Delete clients once dealt with the event
                delete metricEventsRef.current[url];
            }
            // We only want to track the first page load. We only initialize a metric client when this ref is set to false
            lastTrackUrl = url;
            didPageTrackLoad.current = true;
        }
        else {
            // If there is no current metric for this url, it means that the page_load was
            // already completed (i.e. didPageTrackLoad.current = true). In this case, we will
            // ignore subsequent success calls. But if the result was an error we will track it
            // so as to not loose the error reporting.
            if (result !== 'success') {
                // Attempt to set pathname if we know it, but we may not know it due to errors
                var pathname = url === stateRef.current.url ? stateRef.current.pathname : undefined;
                var event_1 = measureEvent('page_load', __assign({ url: url, type: 'unhandled_error', trigger: 'post_load_error', pathname: pathname }, additionalData.current));
                event_1.record(result, data);
            }
            else {
                LOGGER.debug("Page load not started for ".concat(url, ", could not track result of ").concat(result, "."));
            }
        }
        // Clear the current page load url
        stateRef.current.currentPageLoadUrl = undefined;
    }, [measureEvent]);
    /**
     * This function completes a page load and records the metric. This is the only
     * point page loads are filtered/excluded since earlier in the process the pathname
     * name is not always known.
     */
    var trackPageLoad = (0, react_1.useCallback)(function (url, result, data) {
        if (result === void 0) { result = 'success'; }
        if (DD_RUM) {
            DD_RUM.addTiming(customTimingLabel);
        }
        var _a = (data === null || data === void 0 ? void 0 : data.incomplete_measure)
            ? (0, sli_react_metrics_1.getResultFromActionData)(data)
            : { result: result, error_message: data === null || data === void 0 ? void 0 : data.error_message }, result2 = _a.result, error_message = _a.error_message;
        recordPageLoad(url, result2, __assign(__assign({}, data), { error_message: error_message }));
    }, [recordPageLoad, customTimingLabel]);
    // Before starting a page load, the last page load is cleared. This helps ensure
    // each page is properly being tracked. By default, if the previous page load has
    // not completed an error load is tracked, but this can be prevented when the page
    // load should not be tracked.
    var clearPreviousLoad = (0, react_1.useCallback)(function (trackMissedMetric, type, routeUrl, overrides) {
        if (trackMissedMetric === void 0) { trackMissedMetric = true; }
        var url = stateRef.current.currentPageLoadUrl || stateRef.current.url;
        if (url) {
            // Check all events that were initialized during the page life cycle and are still active
            var events = __spreadArray(__spreadArray([], Object.keys(metricEventsRef.current), true), Object.keys(actionsRef), true);
            events.forEach(function (event) {
                var metric = metricEventsRef.current[event] || actionsRef[event];
                if (metric) {
                    var pathname = metric.getData().pathname;
                    // the page url or path should be ignore
                    // we only delete the metric's client for the current url
                    if (stateRef.current.excludedUrls.includes(event) || stateRef.current.excludedPaths.includes(pathname)) {
                        delete metricEventsRef.current[event];
                        // Check if this event is for a page load
                    }
                    else if (!didPageTrackLoad.current && event === stateRef.current.currentPageLoadUrl) {
                        // If the previous page did not finish tracking we still track it but mark it with
                        // the type passed by the consumer.
                        var incompleteMeasureReason = (overrides === null || overrides === void 0 ? void 0 : overrides.manuallyCancelled) === true
                            ? (overrides === null || overrides === void 0 ? void 0 : overrides.reason) || 'Measurement was cancelled by the caller'
                            : 'Measurement could not be completed. The page load initiated but a route change happened before it could be recorded';
                        trackPageLoad(stateRef.current.currentPageLoadUrl, 'success', {
                            incomplete_measure: incompleteMeasureReason,
                            // urlFinal will let us know if the event we are flagging as abandoned|canceled happened at the initial url
                            // If it is different then it most likely mean there was a redirect
                            urlFinal: routeUrl
                        });
                    }
                    else {
                        // This is due to a page not tracking a custom event.
                        var eventName = event;
                        // is there a listener registered by the consumer for this event?
                        if (eventListenersRef.current[eventName]) {
                            // Let the consumer handle it
                            eventListenersRef.current[eventName]();
                            return;
                        }
                        else if (trackMissedMetric) {
                            // There was an unfinished custom action, this is due to a page not calling `endAction` so this will
                            // record a successful action. The duration on this metric will be incorrect, so we want to be able to
                            // exclude it from reports but still have a way to know which pages are not measuring actions correctly
                            LOG2.debug("The ".concat(eventName, " action was not measured. Please make sure your page at ").concat(pathname, " measures events by using the startAction and endAction functions accordingly"));
                            // This means two things. Either the user navigated to another page or the page is not measuring the action correctly.
                            endAction(eventName, {
                                incomplete_measure: 'Measurement could not be completed. It is possible the user navigated to another page before the measurement was completed or that you have a glitch in your instrumentation',
                                pathname: stateRef.current.pathname,
                                url: url,
                                urlFinal: routeUrl
                            });
                        }
                        else {
                            // Delete the current event in the loop
                            delete metricEventsRef.current[event];
                        }
                    }
                }
            });
        }
    }, [trackPageLoad, endAction]);
    var cancelMeasurePageLoad = (0, react_1.useCallback)(function (reason) {
        var url = stateRef.current.currentPageLoadUrl || stateRef.current.url;
        return clearPreviousLoad(false, 'success', url, {
            manuallyCancelled: true,
            reason: reason
        });
    }, [clearPreviousLoad]);
    /**
     * Function to allow consumers to add metadata that is not available when initializing client. A userId/attendeeId
     * is probably a good example
     */
    var updateMetricData = (0, react_1.useCallback)(function (data) {
        if (data === void 0) { data = {}; }
        var url = stateRef.current.currentPageLoadUrl || stateRef.current.url;
        var metric = metricEventsRef.current[url];
        if (metric) {
            metric.update(data);
        }
        else {
            additionalData.current = __assign(__assign({}, additionalData.current), data);
        }
    }, []);
    (0, react_1.useEffect)(function () {
        var _a, _b, _c;
        // When onRouteChangeStart occurs the pathname and query returned
        // by `useRouter` are for the current route, NOT the route we are
        // transitioning to.
        var onRouteChangeStart = function (routeUrl, options) {
            if ((options === null || options === void 0 ? void 0 : options.shallow) && lastTrackUrl !== routeUrl) {
                // if the options.shallow = true, compare by ignoring query parameters
                var lastTrackUrlObj = new URL(lastTrackUrl, window.location.origin);
                var routeUrlObj = new URL(routeUrl, window.location.origin);
                if (lastTrackUrlObj.pathname === routeUrlObj.pathname) {
                    return;
                }
            }
            if (lastTrackUrl === routeUrl) {
                return;
            }
            // If we have a previous metric and the page has not finished loading we want to track this as an abandoned operation
            clearPreviousLoad(true, 'abandoned', routeUrl);
            didPageTrackLoad.current = false;
            stateRef.current.currentPageLoadUrl = routeUrl;
            metricEventsRef.current[routeUrl] = measureEvent('page_load', {
                url: routeUrl,
                trigger: 'route_change'
            });
        };
        // When onRouteChangeComplete occurs the pathname and query returned
        // via `useRouter` have caught up and they are accurate. So at this
        // point we can update the metric event started earlier with the pathname
        var onRouteChangeComplete = function (routeUrl) {
            var metric = metricEventsRef.current[routeUrl];
            var pathname = stateRef.current.pathname;
            lastTrackUrl = routeUrl;
            if (metric) {
                metric.update({ pathname: pathname });
            }
        };
        var onRouteChangeError = function (error, routeUrl) {
            var metric = metricEventsRef.current[routeUrl];
            if (metric) {
                if ((error === null || error === void 0 ? void 0 : error.message) === 'Route Cancelled') {
                    clearPreviousLoad(false, 'cancelled', routeUrl);
                }
                else {
                    trackPageLoad(routeUrl, 'error', {
                        type: 'route_change_error',
                        error_message: error === null || error === void 0 ? void 0 : error.message
                    });
                }
            }
        };
        // eslint-disable-next-line no-unused-expressions
        (_a = router_1.Router.events) === null || _a === void 0 ? void 0 : _a.on('routeChangeStart', onRouteChangeStart);
        // eslint-disable-next-line no-unused-expressions
        (_b = router_1.Router.events) === null || _b === void 0 ? void 0 : _b.on('routeChangeComplete', onRouteChangeComplete);
        // eslint-disable-next-line no-unused-expressions
        (_c = router_1.Router.events) === null || _c === void 0 ? void 0 : _c.on('routeChangeError', onRouteChangeError);
        return function () {
            var _a, _b, _c;
            // eslint-disable-next-line no-unused-expressions
            (_a = router_1.Router.events) === null || _a === void 0 ? void 0 : _a.off('routeChangeStart', onRouteChangeStart);
            // eslint-disable-next-line no-unused-expressions
            (_b = router_1.Router.events) === null || _b === void 0 ? void 0 : _b.off('routeChangeComplete', onRouteChangeComplete);
            // eslint-disable-next-line no-unused-expressions
            (_c = router_1.Router.events) === null || _c === void 0 ? void 0 : _c.off('routeChangeError', onRouteChangeError);
        };
    }, [clearPreviousLoad, measureEvent, trackPageLoad]);
    // Register subscriber for page web vital metrics
    var trackPageLoadWithWebVitals = (0, useCoreWebvitals_1.default)({
        updateMetricData: updateMetricData,
        recordMetric: trackPageLoad,
        includeWebVitals: includeWebVitals,
        customTimingLabel: customTimingLabel
    });
    if (includeWebVitals)
        trackPageLoad = trackPageLoadWithWebVitals;
    return (React.createElement(exports.PageLoadContext.Provider, { value: {
            url: stateRef.current.url,
            pathname: stateRef.current.pathname,
            getBaseProps: getBaseProps,
            trackPageLoad: trackPageLoad,
            cancelMeasurePageLoad: cancelMeasurePageLoad,
            initMetrics: initMetrics,
            measurePageRefetch: measurePageRefetch,
            registerPageAction: registerPageAction,
            unregisterPageAction: unregisterPageAction,
            registerPageActionCallback: registerPageActionCallback
        } }, children));
};
exports.PageLoadProvider = PageLoadProvider;
